// La coquille montée au-dessus des écrans, une fois pour toutes.
//
// C'est ici que vit ce qui doit survivre à la navigation : le rail déplié ou
// replié, le panneau de notifications ouvert, le toast en cours. Les écrans
// s'affichent dans le creux et déclarent ce que la coquille doit montrer.

import "./coquille.css";

import { BandeOnglets } from "@app/components/compta/ui/BandeOnglets";
import { ComptaIcone } from "@app/components/compta/ui/ComptaIcone";
import { ComptaRail } from "@app/components/compta/ui/ComptaRail";
import { ComptaToast } from "@app/components/compta/ui/ComptaToast";
import { AuthContext } from "@app/lib/auth/AuthContext";
import type { UserType } from "@app/types/user";
import { useCallback, useContext, useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";

import { useConfigCoquille } from "./ContexteCoquille";
import { useToastAffiche } from "./ContexteToast";
import type { NomAncre } from "./PortailsCoquille";
import { FournisseurAncres } from "./PortailsCoquille";
import {
  MODULES_COMPTA,
  ONGLETS_CABINET,
  ONGLETS_DOSSIER,
} from "./routes-compta";
import { SelecteurDossier } from "./SelecteurDossier";
import { useNavigationCompta } from "./useNavigationCompta";
import { usePanneauxTransverses } from "./usePanneauxTransverses";

// La coquille ne doit jamais tomber pour un profil incomplet : sans nom, pas
// d'initiales, mais l'écran reste là.
function identiteDe(user: UserType | undefined): {
  initiales: string;
  nomComplet: string;
} {
  const prenom = user?.firstName ?? "";
  const nom = user?.lastName ?? "";
  const nomComplet = user?.fullName ?? `${prenom} ${nom}`.trim();
  const initiales =
    `${prenom.charAt(0)}${nom.charAt(0)}`.trim() || nomComplet.charAt(0);
  return { initiales: initiales.toUpperCase(), nomComplet };
}

export function LayoutCompta() {
  // La coquille est sous le fournisseur d'authentification : elle montre
  // l'utilisateur connecté. Sans lui, des initiales vides plutôt qu'une chute.
  const auth = useContext(AuthContext);
  const identite = identiteDe(auth?.user);
  const [deplie, setDeplie] = useState(false);
  const [ancreSousEntete, setAncreSousEntete] = useState<HTMLElement | null>(
    null
  );
  const [ancreActions, setAncreActions] = useState<HTMLElement | null>(null);
  const [ancrePanneau, setAncrePanneau] = useState<HTMLElement | null>(null);
  const [occupees, setOccupees] = useState<NomAncre[]>([]);

  const navigation = useNavigationCompta();
  // L'espace du dossier ouvert, pour cocher le dossier dans le sélecteur.
  const { spaceId } = useParams<{ spaceId: string }>();
  const panneaux = usePanneauxTransverses();
  const config = useConfigCoquille();
  const { toast, masquer: masquerToast } = useToastAffiche();

  const occuper = useCallback(
    (ancre: NomAncre) => setOccupees((etat) => [...etat, ancre]),
    []
  );
  const liberer = useCallback(
    (ancre: NomAncre) =>
      setOccupees((etat) => {
        const rang = etat.indexOf(ancre);
        return rang === -1 ? etat : etat.toSpliced(rang, 1);
      }),
    []
  );
  const ancres = useMemo(
    () => ({
      noeuds: {
        "sous-entete": ancreSousEntete,
        "actions-scope": ancreActions,
        panneau: ancrePanneau,
      },
      occuper,
      liberer,
    }),
    [ancreSousEntete, ancreActions, ancrePanneau, occuper, liberer]
  );

  // Le temps que le premier écran pose sa configuration.
  const module = config?.module ?? "portefeuille";
  const scope = config?.scope;
  const titreScope = config?.titreScope ?? "";

  // Un sous-en-tête remplace la ligne de titre, comme dans les frames de
  // drill-in ; un panneau posé par l'écran remplace les notifications.
  const sousEnteteOuvert = occupees.includes("sous-entete");
  const panneauDeLEcran = occupees.includes("panneau");

  return (
    <div className="ccoquille">
      <ComptaRail
        actif={module}
        deplie={deplie}
        nonLues={panneaux.nonLues}
        initiales={identite.initiales}
        nom={identite.nomComplet}
        indisponibles={MODULES_COMPTA.filter(
          (module) => !navigation.disponible.module(module)
        )}
        onBasculer={() => setDeplie((etat) => !etat)}
        onNaviguer={navigation.allerAuModule}
        onNotifications={panneaux.ouvrirNotifications}
        onCompte={() => navigation.allerAuModule("parametres")}
      />
      <div className="ccoquille__droite">
        <div className="ccoquille__haut">
          {!sousEnteteOuvert && (
            <div
              className={
                scope
                  ? "ccoquille__scope-ligne"
                  : "ccoquille__scope-ligne ccoquille__scope-ligne--seule"
              }
            >
              {/* Depuis un dossier ouvert, la flèche ramène au portefeuille. */}
              {scope === "dossier" && (
                <button
                  type="button"
                  className="ccoquille__scope-retour"
                  aria-label="Revenir au portefeuille"
                  onClick={navigation.revenirAuPortefeuille}
                >
                  <ComptaIcone nom="arrow-left" taille={16} />
                </button>
              )}
              {/* Le titre déroule les dossiers du cabinet ; sans cabinet, il
                  reste un simple titre. */}
              {scope && auth?.workspace ? (
                <SelecteurDossier
                  owner={auth.workspace}
                  titre={titreScope}
                  dossierActif={
                    config?.scope === "dossier" ? spaceId : undefined
                  }
                  onChoisirDossier={(espace) =>
                    navigation.allerAuDossier(
                      espace,
                      config?.scope === "dossier" ? config.onglet : "apercu"
                    )
                  }
                  onTousLesDossiers={navigation.revenirAuPortefeuille}
                />
              ) : scope ? (
                <button type="button" className="ccoquille__scope">
                  {titreScope}
                  <ComptaIcone nom="caret-down" taille={13} />
                </button>
              ) : (
                <h1 className="ccoquille__scope ccoquille__scope--titre">
                  {titreScope}
                </h1>
              )}
              <span
                className="ccoquille__scope-actions"
                ref={setAncreActions}
              />
            </div>
          )}
          {config?.scope === "cabinet" && (
            <BandeOnglets
              scope="cabinet"
              actif={config.onglet}
              pastilles={config.pastillesOnglets}
              indisponibles={ONGLETS_CABINET.filter(
                (onglet) => !navigation.disponible.ongletCabinet(onglet)
              )}
              onNaviguer={navigation.allerAOngletCabinet}
            />
          )}
          {config?.scope === "dossier" && (
            <BandeOnglets
              scope="dossier"
              actif={config.onglet}
              pastilles={config.pastillesOnglets}
              indisponibles={ONGLETS_DOSSIER.filter(
                (onglet) => !navigation.disponible.ongletDossier(onglet)
              )}
              onNaviguer={navigation.allerAOngletDossier}
            />
          )}
          <div className="ccoquille__slot" ref={setAncreSousEntete} />
        </div>
        <main className="ccoquille__contenu">
          <div className="ccoquille__page">
            <FournisseurAncres {...ancres}>
              <Outlet />
            </FournisseurAncres>
          </div>
        </main>
      </div>
      <div
        className={
          panneauDeLEcran || panneaux.panneau
            ? "ccoquille__cote"
            : "ccoquille__cote ccoquille__cote--vide"
        }
        ref={setAncrePanneau}
      >
        {/* L'écran qui pose son panneau prend la place des notifications. */}
        {!panneauDeLEcran && panneaux.panneau}
      </div>
      {/* La confirmation du dernier geste, d'où qu'il vienne. Activer son
          action la referme : aucun écran n'a plus à y penser. */}
      {toast && (
        <div className="ccoquille__toast">
          <ComptaToast
            titre={toast.titre}
            sousTitre={toast.sousTitre}
            action={toast.action}
            onAction={
              toast.action
                ? () => {
                    masquerToast();
                    toast.onAction?.();
                  }
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
